/**
 * `Move to ▸ Move to board` (`specs/M1-B10-shell-chrome.md` §4.6).
 *
 * monday's `Move to` submenu is exactly three rows — **Move to top · Move to group · Move to
 * board** (`[c:item-menu-move-to]`, 240 × 96) — and the third needs a target *board* **and** a
 * target *group*, because `move_item_to_board` takes both. This is that two-step picker.
 *
 * The move itself is the data source's (`moveItemsToBoard`), so the fixture harness keeps
 * working with no api: nothing under `table/` talks to tRPC directly.
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { useEffect, useState } from 'react';
import { useTRPC } from '../../../../lib/trpc';
import { useQuery } from '@tanstack/react-query';
import { useTableActions, useTableContext } from '../context';
import type { BoardId, GroupId } from '@elizmonday/shared';

interface BoardOption {
  id: string;
  name: string;
}

export function MoveItemsDialog({
  itemIds,
  onClose,
}: {
  itemIds: readonly string[];
  onClose(): void;
}) {
  const { source } = useTableContext();
  const actions = useTableActions();
  const trpc = useTRPC();
  const [boardId, setBoardId] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });


  const boards = useQuery({ ...trpc.boards.list.queryOptions({}), staleTime: 30_000 });
  const groups = useQuery({
    ...trpc.groups.list.queryOptions({ boardId: boardId ?? '0' }),
    enabled: boardId !== null,
    staleTime: 30_000,
  });

  // Picking a board invalidates the group choice; nothing is moved into a stale group.
  useEffect(() => setGroupId(null), [boardId]);

  const options: BoardOption[] = ((boards.data as { id: string; name: string }[] | undefined) ?? []).map(
    (board) => ({ id: String(board.id), name: board.name }),
  );

  const move = async () => {
    if (!boardId || !groupId) return;
    setBusy(true);
    setError(null);
    try {
      await source.moveItemsToBoard({
        boardId: actions.boardId as BoardId,
        itemIds: itemIds as readonly GroupId[] as never,
        targetBoardId: boardId as BoardId,
        targetGroupId: groupId as GroupId,
      });
      await actions.load();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The move failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Move to board"
        data-testid="move-items-dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="ez-board-dialog__title">Move to board</h2>
        <label className="ez-board-dialog__field">
          Board
          <select
            data-testid="move-items-board"
            value={boardId ?? ''}
            onChange={(event) => setBoardId(event.target.value || null)}
          >
            <option value="">Choose a board…</option>
            {options
              .filter((board) => board.id !== String(actions.boardId))
              .map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
          </select>
        </label>
        <label className="ez-board-dialog__field">
          Group
          <select
            data-testid="move-items-group"
            disabled={boardId === null}
            value={groupId ?? ''}
            onChange={(event) => setGroupId(event.target.value || null)}
          >
            <option value="">Choose a group…</option>
            {((groups.data as { id: string; title: string }[] | undefined) ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.title}
              </option>
            ))}
          </select>
        </label>
        {/* monday drops a column it cannot match by title; saying so before the click is kinder. */}
        <p className="ez-board-dialog__hint">
          Columns are matched by title. A column the target board does not have is dropped.
        </p>
        {error ? (
          <p className="ez-board-dialog__hint" role="alert">
            {error}
          </p>
        ) : null}
        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ez-board-dialog__save"
            data-testid="move-items-submit"
            disabled={busy || boardId === null || groupId === null}
            onClick={() => void move()}
          >
            Move
          </button>
        </footer>
      </div>
    </div>
  );
}
